import { KDataTable, KFullscreenDialog } from "kinnect-general";
import { YardScheduleWeather } from "kinnect-models";
import {
	formatCardinalDirection,
	formatDate,
	formatTime
} from "kinnect-formatters";
import {
	VAlert,
	VCardText,
	VCol,
	VDivider,
	VIcon,
	VRow
} from "vuetify/lib";

export default {
	name: "KWeatherDialog",

	props: {
		...KFullscreenDialog.options.props,

		schedule: Object,

		value: {
			default: "auto",
			type: [ String, Boolean ],
			validator: value => typeof value === "boolean" || value === "auto"
		}
	},

	data: () => ( {
		autoValue: false,
		weather: null,
		timeFormat: "h a",
		headers: {
			daily: [
				{
					text: "Day",
					value: "time",
					width: "13em"
				},
				{
					text: "Details",
					value: "summary"
				},
				{
					text: "Low -> High",
					value: "temperatureMin",
					width: "13em"
				},
				{
					text: "Precip.",
					value: "precipProbability"
				},
				{
					text: "Wind",
					value: "windSpeed"
				}
			],
			hourly: [
				{
					text: "Time",
					value: "time",
					width: "13em"
				},
				{
					text: "Details",
					value: "summary"
				},
				{
					text: "Temp",
					value: "temperature"
				},
				{
					text: "Feels",
					value: "apparentTemperature"
				},
				{
					text: "Precip.",
					value: "precipProbability"
				},
				{
					text: "Wind",
					value: "windSpeed"
				}
			]
		}
	} ),

	computed: {
		autoModel() {
			return this.value === "auto";
		},

		internalValue: {
			get() {
				if ( this.autoModel ) {
					return this.autoValue;
				}

				return this.value;
			},

			set( val ) {
				if ( this.autoModel ) {
					this.autoValue = val;
				}

				this.$emit( "input", val );
			}
		},

		internalTitle() {
			if ( this.title ) {
				return this.title;
			} else if ( this.schedule && this.schedule.address ) {
				return `Weather for ${this.schedule.address.address}, ${this.schedule.address.city}, ${this.schedule.address.state} ${this.schedule.address.zip}`;
			}

			return "Weather";
		}
	},

	watch: {
		internalValue( val ) {
			if ( val ) {
				this.fetchWeather();
			}
		}
	},

	methods: {
		async fetchWeather() {
			this.weather = await YardScheduleWeather.find( this.schedule.id );
		}
	},

	render() {
		return <KFullscreenDialog
			attrs={{
				...this.$props,
				loading: this.$wait.is( "find schedule weather" ),
				title: this.internalTitle
			}}
			on={{ ...this.$listeners }}
			vModel={this.internalValue}
			scopedSlots={{
				default: () =>
					this.weather && <VCardText class="k-weather-dialog">
						{this.weather.alerts && <div>
							{this.weather.alerts.map( ( alert, i ) =>
								<VAlert
									key={`${i}-alert`}
									color="error"
									dark
									icon="warning"
								>
									<h4>{alert.title}</h4>
									<p>Expires {formatDate( alert.expires * 1000, "h:mm a 'on' MMM do, yyyy" )}</p>
									<p>{alert.description}</p>
									<p><a class="white--text" href={alert.uri} target="_blank">View more info</a></p>
								</VAlert>
							)}

							<VDivider class="my-5"></VDivider>
						</div>}

						<KDataTable
							headers={this.headers.daily}
							hideDefaultFooter
							items={this.weather.daily.data}
							itemsPerPage={-1}
							sortBy="time"
							scopedSlots={{
								title: () => "Daily",

								subtitle: () => this.weather.daily.summary,

								"item.time": ( { item } ) => [
									<div class="float-left">
										<h3>{formatDate( item.time * 1000, "EEE" )}</h3>
										<h4>{formatDate( item.time * 1000, "MMM do" )}</h4>
									</div>,

									<img class="float-right" src={require( `@/assets/icons/weather/${item.icon}.png` )} />
								],

								"item.temperatureMin": ( { item } ) =>
									<VRow>
										<VCol cols={4}>
											<h3>{Math.round( item.temperatureMin )}<sup>&deg;</sup></h3>

											<span class="text-no-wrap">
												{formatTime( item.temperatureMinTime * 1000, this.timeFormat )}
											</span>
										</VCol>

										<VCol cols={4}>
											<VIcon>arrow_forward</VIcon>
										</VCol>

										<VCol cols={4}>
											<h3>{Math.round( item.temperatureMax )}<sup>&deg;</sup></h3>

											<span class="text-no-wrap">
												{formatTime( item.temperatureMaxTime * 1000, this.timeFormat )}
											</span>
										</VCol>
									</VRow>,

								"item.precipProbability": ( { item } ) => [
									`${Math.round( item.precipProbability * 100 )}%`,

									Boolean( item.precipProbability ) && <span class="d-block">
										{formatTime( item.precipIntensityMaxTime * 1000, this.timeFormat )} peak
										<span> - </span>
										{Math.round( item.precipIntensityMax * 100 ) / 100} in/hr
									</span>
								],

								"item.windSpeed": ( { item } ) => (
									item.windSpeed >= 0.5
										? `${formatCardinalDirection( item.windBearing )} at ${Math.round( item.windSpeed )} mph`
										: "0 mph"
								)

							}}
						></KDataTable>

						<VDivider class="my-5"></VDivider>

						<KDataTable
							headers={this.headers.hourly}
							hideDefaultFooter
							items={this.weather.hourly.data}
							itemsPerPage={-1}
							sortBy="time"
							scopedSlots={{
								title: () => "Hourly",

								subtitle: () => this.weather.hourly.summary,

								"item.time": ( { item } ) => [
									<div class="float-left">
										<h3>{formatTime( item.time * 1000, this.timeFormat )}</h3>
										<h4>{formatDate( item.time * 1000, "EEE" )}</h4>
									</div>,

									<img class="float-right" src={require( `@/assets/icons/weather/${item.icon}.png` )} />
								],

								"item.temperature": ( { item } ) =>
									<span>
										{Math.round( item.temperature )}
										<sup>&deg;</sup>
									</span>,

								"item.apparentTemperature": ( { item } ) =>
									<span>
										{Math.round( item.apparentTemperature )}
										<sup>&deg;</sup>
									</span>,

								"item.precipProbability": ( { item } ) => [
									`${Math.round( item.precipProbability * 100 )}%`,

									Boolean( item.precipProbability ) && <span class="d-block">
										{Math.round( item.precipIntensity * 100 ) / 100} in/hr
									</span>
								],

								"item.windSpeed": ( { item } ) => (
									item.windSpeed >= 0.5
										? `${formatCardinalDirection( item.windBearing )} at ${Math.round( item.windSpeed )} mph`
										: "0 mph"
								)
							}}
						></KDataTable>
					</VCardText>,

				...this.$scopedSlots
			}}
		></KFullscreenDialog>;
	}
};
